import { EvaluationColor } from '@repo/types';

import { evaluationColorToCSSVariableMap } from '../../helpers/evaluationColors';

export type RangeBarRange = {
  range_lower: number;
  range_upper: number;
  evaluation_color: EvaluationColor;
};

export type PreparedRange = RangeBarRange & {
  percentage: number;
  isSampleRange: boolean;
  samplePosition: number;
  breakpointLabel: string | null;
  breakpointAbove: boolean;
};

const HALF_PILL_PX = 25;

const clampRanges = (
  ranges: RangeBarRange[],
  min: number | undefined,
  max: number | undefined,
): RangeBarRange[] => {
  if (min === undefined && max === undefined) return ranges;
  const out: RangeBarRange[] = [];
  for (const r of ranges) {
    if (min !== undefined && r.range_upper <= min) continue;
    if (max !== undefined && r.range_lower >= max) continue;
    out.push({
      ...r,
      range_lower:
        min !== undefined && r.range_lower < min ? min : r.range_lower,
      range_upper:
        max !== undefined && r.range_upper > max ? max : r.range_upper,
    });
  }
  return out;
};

const computeSamplePosition = (
  range: { range_lower: number; range_upper: number },
  value: number,
): number => {
  if (value < range.range_lower) return 1;
  if (value > range.range_upper) return 99;
  const width = range.range_upper - range.range_lower;
  return width ? ((value - range.range_lower) * 100) / width : 0;
};

type PrepareRangesInput = {
  ranges: RangeBarRange[];
  value: number;
  min?: number;
  max?: number;
  formatBreakpoint?: (value: number) => string;
};

export const prepareRanges = ({
  ranges,
  value,
  min,
  max,
  formatBreakpoint = String,
}: PrepareRangesInput): PreparedRange[] | null => {
  if (!ranges?.length) return null;

  const clamped = clampRanges(ranges, min, max);
  if (!clamped.length) return null;

  const scaleMin = clamped[0].range_lower;
  const scaleMax = clamped[clamped.length - 1].range_upper;
  const totalWidth = scaleMax - scaleMin;
  const valueBelowMin = value < scaleMin;
  const valueAboveMax = value > scaleMax;
  const valueOutside = valueBelowMin || valueAboveMax;

  let breakpointIndex = 0;
  return clamped.map((range, idx) => {
    const showBreakpoint =
      idx > 0 && range.evaluation_color !== clamped[idx - 1].evaluation_color;
    const breakpointAbove = showBreakpoint && breakpointIndex % 2 === 1;
    const breakpointLabel = showBreakpoint
      ? formatBreakpoint(range.range_lower)
      : null;
    if (showBreakpoint) breakpointIndex++;

    const isSampleRange = valueOutside
      ? (valueBelowMin && idx === 0) ||
        (valueAboveMax && idx === clamped.length - 1)
      : range.range_lower <= value && range.range_upper >= value;

    return {
      ...range,
      percentage: totalWidth
        ? ((range.range_upper - range.range_lower) * 100) / totalWidth
        : 0,
      isSampleRange,
      samplePosition: isSampleRange ? computeSamplePosition(range, value) : 0,
      breakpointLabel,
      breakpointAbove,
    };
  });
};

export function calculateLeft(
  isFirst: boolean,
  isLast: boolean,
  samplePosition: number,
): string {
  if (!isFirst && !isLast) return `${samplePosition}%`;

  if (isFirst) return `max(${HALF_PILL_PX}px, ${samplePosition}%)`;

  return `min(calc(100% - ${HALF_PILL_PX}px), ${samplePosition}%)`;
}

type ColoredBarStyles = NonNullable<
  React.HTMLAttributes<HTMLSpanElement>['style']
>;

// Assumes the scale is symmetric around 0 (runs -max…+max), so the neutral
// origin sits at the track's geometric center (50%). If asymmetric scales are
// ever needed, derive the center from scaleMin/scaleMax instead of hardcoding
// 50% and scale by the full span instead of `max`.
export function computeColoredBarStyles(
  value: number,
  max: number,
  valueMarkerColor: EvaluationColor,
): ColoredBarStyles {
  const coloredBarWidth = `${((Math.min(Math.abs(value), max) / max) * 100) / 2}%`;
  const coloredBarPosition =
    value < 0 ? { left: `calc(50% - ${coloredBarWidth})` } : { left: '50%' };

  const gradientBackgroundDirection = value > 0 ? 'to left' : 'to right';

  const gradientBackground = `linear-gradient(${gradientBackgroundDirection}, ${evaluationColorToCSSVariableMap[valueMarkerColor]} 50%, transparent)`;

  return {
    ...coloredBarPosition,
    width: coloredBarWidth,
    background: gradientBackground,
  };
}
